<script setup>
import { useTemplateRef } from 'vue'

const show = defineModel({ type: Boolean, default: true }) // Opcional, default true

const props = defineProps({
  title: {
    type: String,
    default: '',
  },
  headerIcon: {
    type: String,
    default: '',
  },
  size: {
    type: String,
    default: 'md',
    validator: v => ['sm', 'md', 'lg'].includes(v),
  },
  showHeader: {
    type: Boolean,
    default: true,
  },
  showFooter: {
    type: Boolean,
    default: true,
  },
  closeable: {
    type: Boolean,
    default: true,
  },
  closeOnBackdrop: {
    type: Boolean,
    default: true,
  },
})

const emit = defineEmits(['close'])

const containerRef = useTemplateRef('containerRef')

const close = () => {
  if (!props.closeable) return
  show.value = false
  emit('close')
}

const onBackdropClick = () => {
  if (props.closeOnBackdrop) close()
}

const onKeydown = event => {
  if (event.key === 'Escape') {
    close()
    return
  }

  if (event.key !== 'Tab') return

  const focusable = containerRef.value?.querySelectorAll(
    'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
  )

  if (!focusable || focusable.length < 2) {
    event.preventDefault()
    return
  }

  const first = focusable[0]
  const last = focusable[focusable.length - 1]

  if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault()
    first.focus()
  } else if (event.shiftKey && document.activeElement === first) {
    event.preventDefault()
    last.focus()
  }
}
</script>

<template>
  <Teleport to="body">
    <Transition name="modal">
      <div v-if="show" class="fixed inset-0 z-[70] flex items-center justify-center">
        <!-- Backdrop -->
        <div class="absolute inset-0 bg-black/25 backdrop-blur-sm" @click="onBackdropClick" />

        <!-- Modal -->
        <div
          ref="containerRef"
          role="dialog"
          aria-modal="true"
          class="relative mx-4 flex flex-col rounded-3xl bg-white shadow-xl"
          :class="{
            'max-w-[600px]': size === 'sm',
            'max-w-[690px]': size === 'md',
            'max-w-[900px]': size === 'lg',
          }"
          @keydown="onKeydown"
        >
          <!-- Header -->
          <div
            v-if="showHeader"
            class="border-neutral-20 flex shrink-0 items-center border-b"
            :class="{
              'min-h-[52px] px-4 py-2': size === 'sm',
              'min-h-[70px] px-6 py-2': size === 'md',
              'min-h-[76px] px-10 py-2': size === 'lg',
            }"
          >
            <slot name="header">
              <div class="flex grow items-center justify-between">
                <div class="flex items-center gap-2">
                  <ImoIcon v-if="headerIcon" :name="headerIcon" :size="20" />
                  <h4 class="text-primary-90 font-semibold">{{ title }}</h4>
                </div>
                <button
                  v-if="closeable"
                  class="text-primary-90 outline-hidden focus:outline-hidden"
                  @click="close"
                >
                  <ImoIcon name="close" />
                </button>
              </div>
            </slot>
          </div>

          <!-- Body -->
          <div
            class="flex w-full overflow-x-hidden overflow-y-auto"
            :class="{
              'min-h-[150px] ': size === 'sm',
              'min-h-[376px] px-6 py-2': size === 'md',
              'min-h-[656px] px-10 py-2': size === 'lg',
            }"
          >
            <slot name="body" />
          </div>

          <!-- Footer -->
          <div
            v-if="showFooter"
            class="border-neutral-20 flex w-full shrink-0 items-center border-t"
            :class="{
              'min-h-[52px] px-4 py-2': size === 'sm',
              'min-h-[70px] px-6 py-2': size === 'md',
              'min-h-[76px] px-10 py-2': size === 'lg',
            }"
          >
            <slot name="footer">
              <div class="flex grow justify-end">
                <ImoButton text="Fechar" variant="secondary" @click="close" />
              </div>
            </slot>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.2s ease;
}

.modal-enter-active .relative,
.modal-leave-active .relative {
  transition:
    transform 0.2s ease,
    opacity 0.2s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

.modal-enter-from .relative,
.modal-leave-to .relative {
  transform: scale(0.95);
  opacity: 0;
}
</style>
